// ── SeenStore — anti-repeat question memory ────────────────────────────────
// Tracks which question IDs a user has seen per (examProgram, year, subject, mode).
// Storage: localStorage now; designed so a remote Supabase adapter can replace
// localSeenStore without changing any call sites.

export interface SeenEntry {
  id: string;
  timestamp: number;
}

export interface SeenScope {
  examProgram: string;
  year: string;
  subject: string;
  mode: string;
}

export interface SeenStore {
  getSeenIds(scope: SeenScope): string[];
  getSeenWithTimestamps(scope: SeenScope): SeenEntry[];
  markSeen(scope: SeenScope, ids: string[]): void;
  reset(scope: SeenScope): void;
  resetAll(): void;
}

function scopeKey(scope: SeenScope): string {
  return `lumio_seen:${scope.examProgram}:${scope.year}:${scope.subject}:${scope.mode}`;
}

function loadEntries(key: string): SeenEntry[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as SeenEntry[];
  } catch {
    return [];
  }
}

function saveEntries(key: string, entries: SeenEntry[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(entries));
  } catch (e) {
    console.warn('[SeenStore] localStorage quota exceeded — degrading to no-memory mode', e);
  }
}

export const localSeenStore: SeenStore = {
  getSeenIds(scope) {
    return loadEntries(scopeKey(scope)).map(e => e.id);
  },

  getSeenWithTimestamps(scope) {
    return loadEntries(scopeKey(scope));
  },

  markSeen(scope, ids) {
    if (!ids.length) return;
    const key = scopeKey(scope);
    const existing = loadEntries(key);
    const existingIds = new Set(existing.map(e => e.id));
    const now = Date.now();
    const fresh = ids
      .filter(id => !existingIds.has(id))
      .map(id => ({ id, timestamp: now }));
    if (fresh.length === 0) return;
    saveEntries(key, [...existing, ...fresh]);
  },

  reset(scope) {
    try { localStorage.removeItem(scopeKey(scope)); } catch { /* ignore */ }
  },

  resetAll() {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith('lumio_seen:')) keys.push(k);
      }
      keys.forEach(k => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
    } catch { /* ignore */ }
  },
};
